import { Link } from "react-router-dom";
import PublicShell, { useSiteContent } from "@/components/layout/PublicShell";
import { ArrowRight, Users, Target, Check } from "lucide-react";

export default function ServiceModelsPage() {
  const content = useSiteContent();
  const sm = content?.service_models || {};
  const staff = sm.staff_aug || {};
  const delivery = sm.delivery_services || {};
  const compare = sm.compare || [];

  return (
    <PublicShell>
      <section className="relative border-b border-border overflow-hidden" data-testid="models-hero">
        <div className="absolute inset-0 bg-gridlines opacity-50 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 lg:pt-28 pb-16 lg:pb-20">
          <div className="overline mb-4">Service Models</div>
          <h1 className="font-heading text-5xl lg:text-7xl font-bold tracking-tighter leading-[1] text-obsidian max-w-5xl">
            {sm.page_title || "Service Models"}
          </h1>
          <p className="mt-6 text-lg lg:text-2xl text-muted-foreground max-w-3xl">{sm.page_subtitle}</p>
        </div>
      </section>

      {/* Two pillars side by side */}
      <section className="border-b border-border" data-testid="models-detail">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2">
          <div className="p-8 lg:p-16 border-b lg:border-b-0 lg:border-r border-border bg-white" data-testid="model-staff-aug">
            <Users className="h-7 w-7 text-execution mb-6" />
            <div className="overline mb-3">{staff.tagline || "Capability Provision"}</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05] text-obsidian">
              {staff.title}
            </h2>
            <p className="mt-6 text-base lg:text-lg text-foreground/80 leading-relaxed">{staff.intro}</p>
            <ul className="mt-8 space-y-3">
              {(staff.highlights || []).map((h) => (
                <li key={h} className="flex gap-3 text-sm text-foreground/80">
                  <Check className="h-4 w-4 text-execution mt-0.5 shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 p-4 border-l-2 border-execution bg-muted text-sm text-foreground/80">
              <span className="font-mono text-[10px] tracking-[0.2em] text-execution uppercase block mb-1">Right when</span>
              {staff.fit}
            </div>
          </div>

          <div className="p-8 lg:p-16 bg-obsidian text-white relative overflow-hidden" data-testid="model-delivery">
            <div className="absolute inset-0 bg-gridlines-dark opacity-40 pointer-events-none" />
            <div className="relative">
              <Target className="h-7 w-7 text-execution mb-6" />
              <div className="overline-light mb-3">{delivery.tagline || "Outcome Ownership"}</div>
              <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
                {delivery.title}
              </h2>
              <p className="mt-6 text-base lg:text-lg text-white/80 leading-relaxed">{delivery.intro}</p>
              <ul className="mt-8 space-y-3">
                {(delivery.highlights || []).map((h) => (
                  <li key={h} className="flex gap-3 text-sm text-white/85">
                    <Check className="h-4 w-4 text-execution mt-0.5 shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-4 border-l-2 border-execution bg-white/5 text-sm text-white/85">
                <span className="font-mono text-[10px] tracking-[0.2em] text-execution uppercase block mb-1">Right when</span>
                {delivery.fit}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="section" data-testid="models-compare">
        <div className="overline mb-4">Side by side</div>
        <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05] mb-10 lg:mb-14">
          Two engagement models, one delivery standard.
        </h2>
        <div className="border border-border overflow-x-auto bg-white">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="bg-obsidian text-white">
                <th className="px-6 py-4 text-left font-mono text-[11px] uppercase tracking-[0.2em] font-medium w-1/4">Dimension</th>
                <th className="px-6 py-4 text-left font-heading text-base font-semibold">
                  <div className="flex items-center gap-2"><Users className="h-4 w-4 text-execution" /> Staff Augmentation</div>
                </th>
                <th className="px-6 py-4 text-left font-heading text-base font-semibold">
                  <div className="flex items-center gap-2"><Target className="h-4 w-4 text-execution" /> Delivery Services</div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {compare.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "" : "bg-muted/40"} data-testid={`compare-row-${i}`}>
                  <td className="px-6 py-4 font-medium">{row.label}</td>
                  <td className="px-6 py-4 text-foreground/80">{row.staff_aug}</td>
                  <td className="px-6 py-4 text-foreground/80">{row.delivery_services}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="border-t border-border bg-obsidian text-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <h3 className="md:col-span-8 font-heading text-3xl lg:text-5xl font-bold tracking-tighter leading-[1.02]">
            Tell us where the program needs us.
          </h3>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/engage" className="btn-accent">
              Engage Nexra
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
